import type { ErrorMessages } from '../types';

/**
 * German-canonical error strings. Per ADR 0009:
 *
 *  - The shape is enforced by `satisfies ErrorMessages`: every code in the
 *    api-contract `ErrorCode` union must have a leaf here, or `tsc` fails.
 *  - Reserved top-level keys (`generic`, `support`, `network`, `zod`, `app`) are
 *    NOT enforced by the type — they are enforced at runtime by the
 *    coverage script and at lint time by `scripts/check-reserved-i18n-keys.ts`
 *    (a code in the api-contract registry MUST NOT use any of these as its module segment).
 *  - Register: informal `du`, brand `immoOS`. See ADR 0009 §German register
 *    and `.docs/product/prd-conventions/drafting-user-facing-copy.md`.
 *  - ICU MessageFormat is the message format. `{name}` interpolation reads
 *    keys from the `details` object on the wire-error envelope; plurals use
 *    `{count, plural, one {…} other {…}}`.
 */
export const errors = {
  generic: {
    unexpected: 'Etwas ist schiefgelaufen — bitte versuche es erneut.',
  },
  support: {
    'failure-id-prefix': 'Fehler-Kennung',
    'copy-failure-id': 'Kennung kopieren',
    'failure-id-copied': 'Kennung kopiert.',
  },
  /**
   * Client-side only (IMM-588.2): the running bundle is older than the API it is
   * talking to, so a reload — not a retry — is what fixes it. Never crosses the
   * wire; raised by the typed client in the browser.
   */
  app: {
    outdated: 'immoOS wurde soeben aktualisiert. Lade die Seite neu, um weiterzuarbeiten.',
  },
  network: {
    offline: 'Du bist offline. Bitte überprüfe deine Internetverbindung.',
    // The request never reached a response, but the browser still has a
    // connection — i.e. immoOS is unreachable, not the user's line. Telling
    // someone who is demonstrably online to check their internet is worse
    // than saying nothing, so this is the default and `offline` is used only
    // when the browser reports the link is genuinely down.
    unreachable: 'immoOS ist gerade nicht erreichbar. Bitte versuche es in einem Moment erneut.',
    timeout: 'Die Anfrage hat zu lange gedauert. Bitte versuche es erneut.',
  },
  /**
   * Translations for Zod issue codes (`zod` v4 `ZodIssueCode` values).
   * Consumed by the FE when an `error.code` arrives as `'zod.<issueCode>'`
   * or when client-side validation surfaces a Zod issue directly.
   */
  zod: {
    invalid_type: 'Ungültiger Typ.',
    invalid_literal: 'Ungültiger Wert.',
    custom: 'Ungültige Eingabe.',
    invalid_union: 'Ungültige Eingabe.',
    invalid_union_discriminator: 'Ungültiger Wert für die Auswahl.',
    invalid_enum_value: 'Ungültiger Auswahlwert.',
    unrecognized_keys: 'Unerwartete Felder.',
    invalid_arguments: 'Ungültige Argumente.',
    invalid_return_type: 'Ungültiger Rückgabetyp.',
    invalid_date: 'Ungültiges Datum.',
    invalid_string: 'Ungültiger Text.',
    too_small: 'Wert ist zu klein.',
    too_big: 'Wert ist zu groß.',
    invalid_intersection_types: 'Ungültige Eingabe.',
    not_multiple_of: 'Wert muss ein Vielfaches sein.',
    not_finite: 'Wert muss endlich sein.',
  },
  /**
   * Request-time failures the Fastify error hook classifies from its own table
   * (IMM-584) — raised before any handler runs, so they belong to no bounded
   * context. `shared.request.invalid` is deliberately absent: Zod failures
   * render through the per-issue `errors.zod.*` entries above (ADR 0014).
   */
  shared: {
    request: {
      'unsupported-media-type': 'Dieser Inhaltstyp wird nicht unterstützt.',
      'malformed-body': 'Der Anfrageinhalt konnte nicht gelesen werden.',
      'malformed-url': 'Die Anfrage-Adresse ist ungültig.',
      'body-too-large': 'Der Anfrageinhalt ist zu groß.',
      'uri-too-long': 'Die Anfrage-Adresse ist zu lang.',
    },
    standort: {
      'reassignment-denied':
        'Dazu fehlt dir die Berechtigung: Du kannst diesen Datensatz nicht an einen anderen Standort verschieben.',
    },
  },
  identity: {
    user: {
      'email-already-taken': 'Diese E-Mail-Adresse ist bereits vergeben.',
      'not-found': 'Dieser Benutzer wurde nicht gefunden.',
    },
    auth: {
      'sign-in-failed': 'Anmeldung fehlgeschlagen — bitte erneut versuchen.',
      'refresh-failed': 'Sitzung konnte nicht erneuert werden — bitte erneut anmelden.',
      'state-mismatch': 'Der Anmeldevorgang ist abgelaufen — bitte erneut starten.',
      'user-not-provisioned': 'Dein Konto ist noch nicht freigeschaltet — bitte kontaktiere unser Vertriebsteam.',
      'cannot-resolve-user': 'Dein Konto wurde nicht gefunden — bitte erneut anmelden.',
    },
    // Sandbox sign-in is engineer-facing tooling: English in both locales (PRD Copy, slice D5). Do not translate.
    'sandbox-gate': {
      'account-not-authorised': 'This Google account is not authorised for the sandbox.',
      'rate-limited': 'Too many sign-in attempts. Try again in a minute.',
      required: 'Sign in with your immoOS Google account first.',
    },
    'sandbox-member': {
      'not-found': 'No member with this e-mail in the sandbox.',
    },
    workspace: {
      'name-too-short': 'Der Workspace-Name muss mindestens 2 Zeichen lang sein.',
      'name-too-long': 'Der Workspace-Name darf höchstens 80 Zeichen lang sein.',
      'not-found': 'Dieser Workspace wurde nicht gefunden.',
      // Identical to `not-found` on purpose: a non-member must not learn the workspace exists.
      'membership-not-found': 'Dieser Workspace wurde nicht gefunden.',
    },
    'web-address': {
      'invalid-format':
        'Die Web-Adresse darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten und muss 3 bis 40 Zeichen lang sein.',
      taken: 'Diese Web-Adresse ist schon vergeben. Bitte wähle eine andere.',
    },
    organization: {
      'not-found': 'Diese Organisation wurde nicht gefunden.',
      cycle: 'Ein Standort kann nicht sich selbst oder einem seiner untergeordneten Standorte zugeordnet werden.',
      'duplicate-name': 'In diesem Workspace existiert bereits ein Standort mit diesem Namen.',
      'main-cannot-have-parent': 'Der Hauptstandort kann keinem anderen Standort untergeordnet werden.',
    },
    'coverage-postal-code': {
      'not-assigned': 'Diese PLZ ist diesem Standort nicht zugeordnet.',
    },
    member: {
      'not-found': 'Dieses Mitglied wurde nicht gefunden.',
      'affiliation-required': 'Mindestens ein Standort muss ausgewählt sein.',
      'affiliation-requires-role':
        'Für mindestens einen ausgewählten Standort ist noch keine Rolle vergeben. Ein Mitglied kann nur Standorten zugeordnet werden, in denen es eine Rolle hat — vergib die Rolle zuerst über „Standorte zuweisen“.',
      'already-active': 'Dieses Mitglied ist bereits aktiv. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'already-deactivated':
        'Dieses Mitglied ist bereits deaktiviert. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-deactivate-last-owner':
        'Ein Workspace braucht mindestens ein aktives Mitglied mit workspaceweiten Rechten. Vergib die Rolle Eigentümer oder Admin zuerst an ein anderes Mitglied, bevor du dieses deaktivierst.',
      'cannot-deactivate-self': 'Du kannst dich nicht selbst deaktivieren.',
      'cannot-remove-self':
        'Du kannst dich nicht selbst aus einem Standort entfernen. Das kann nur ein anderes Mitglied mit der nötigen Berechtigung.',
      'duplicate-email': 'Dieses Mitglied gehört bereits zum Workspace.',
      'primary-not-in-affiliation': 'Der primäre Standort muss Teil der Standortzugehörigkeit sein.',
      'workspace-must-retain-owner':
        'Ein Workspace braucht mindestens ein Mitglied mit workspaceweiten Rechten. Vergib die Rolle Eigentümer oder Admin zuerst an ein anderes Mitglied, bevor du sie hier entziehst.',
    },
    invitation: {
      'duplicate-pending': 'Für diese E-Mail-Adresse besteht bereits eine ausstehende Einladung.',
      'not-found': 'Diese Einladung wurde nicht gefunden.',
      'not-pending': 'Diese Einladung ist nicht mehr ausstehend.',
    },
    'member-profile': {
      'invalid-photo': 'Bitte lade ein JPG-, PNG- oder WebP-Bild bis 5 MB hoch.',
    },
    // Operator-facing (admin.immoos.ai), so still German. The credential's
    // AUTHENTICATION failures deliberately have no code and no string here —
    // an unknown, revoked, expired or malformed credential is a flat 401 read
    // by a tool client, never by a person, and giving each its own message
    // would let the surface be probed.
    // Tool-client-facing (IMM-522.2) — present for dictionary completeness
    // only; no human ever reads these, the caller is always an MCP client.
    mcp: {
      'protocol-version-unsupported': 'Diese Protokollversion wird nicht unterstützt. Unterstützt: {supported}.',
      'header-body-mismatch': 'Die Anfrage ist ungültig: {header} stimmt nicht mit dem Inhalt überein.',
      'origin-not-allowed': 'Diese Herkunft ist für diesen Zugriff nicht zugelassen.',
    },
    'mcp-access': {
      'name-already-used': 'Du hast bereits einen Zugang mit diesem Namen.',
      'credential-limit-reached':
        'Du hast das Maximum von {max} aktiven Zugängen erreicht. Widerrufe einen bestehenden Zugang, um einen neuen zu erstellen.',
      'not-found': 'Dieser Zugang existiert nicht mehr.',
      'surface-disabled':
        'Der MCP-Zugriff ist derzeit für die gesamte Plattform deaktiviert. Du kannst keinen neuen Zugang erstellen, solange er deaktiviert ist.',
      'already-revoked': 'Dieser Zugang wurde bereits widerrufen.',
    },
    'valuation-agent-token': {
      'already-issued': 'Es ist bereits ein Token ausgestellt. Lade die Seite neu.',
      'no-permissions': 'Wähle mindestens eine Berechtigung.',
      'not-active': 'Dieser Token ist nicht mehr aktiv. Lade die Seite neu.',
    },
    'valuation-agent': {
      'permission-missing': 'Für diese Aktion fehlt dem Bewertungs-Agent die Berechtigung.',
    },
    // Erreichbarkeitsdiagnose der MCP-Oberfläche (IMM-625.4). Reine
    // Operator-Copy — die Meldungen erscheinen nur im Admin-Bereich.
    'mcp-surface': {
      'credential-not-found': 'Diesen Zugang gibt es nicht mehr.',
      'seat-required': 'Dieser Zugang gilt für mehrere Standorte. Wähle den Standort, um den es geht.',
    },
    // Per-Werkzeug-Freigabe der MCP-Oberfläche (IMM-625.2).
    'mcp-tool-exposure': {
      'not-found': 'Dieses Werkzeug gibt es nicht mehr im Katalog. Lade die Seite neu.',
      'not-stale':
        'Dieses Werkzeug ist noch im Katalog — der Eintrag kann nicht entfernt werden. Entziehe stattdessen die Freigabe.',
      // Die Stapel-Freigabe ist ganz oder gar nicht (IMM-769.7): „Es wurde
      // nichts geändert" ist die tragende Hälfte der Aussage.
      'batch-unknown-tool':
        'Mindestens ein Werkzeug aus der Auswahl gibt es nicht mehr im Katalog. Es wurde nichts geändert — lade die Seite neu.',
    },
    'api-key': {
      'name-taken': 'Ein aktiver Admin-API-Schlüssel mit dem Namen „{name}“ existiert bereits.',
      'workspace-unknown': 'Einer der gewählten Workspaces existiert nicht mehr.',
      'cannot-edit-service-key':
        'Ein Admin-API-Schlüssel kann nicht geändert werden. Widerrufe ihn und stelle einen neuen aus.',
      // MCP-fähige Schlüssel brauchen eine begrenzte Gültigkeit (IMM-601.1).
      'expiry-required': 'Wähle, wie lange dieser Schlüssel gültig sein soll.',
      // Rollierendes 24-h-Tageslimit MCP-fähiger Schlüssel (IMM-601.2). Nur für
      // die Vollständigkeit des Wörterbuchs — die Oberfläche ist ein MCP-Client
      // und liest die englische Wire-Message.
      'daily-limit-reached':
        'Dieses Tageslimit von {limit} Anfragen ist erreicht. Versuche es in {retryInMinutes} Minuten erneut.',
      'not-found': 'Dieser API-Schlüssel wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-edit-revoked':
        'Dieser API-Schlüssel wurde widerrufen und kann nicht mehr bearbeitet werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'invalid-description': 'Die Beschreibung des API-Schlüssels ist zu lang. Kürze sie.',
      'invalid-name': 'Der Name des API-Schlüssels fehlt oder ist zu lang.',
      'invalid-rate-limit': 'Das Anfragelimit des API-Schlüssels liegt außerhalb des erlaubten Bereichs.',
      'no-eligible-owner':
        'Dieser Workspace hat kein aktives Mitglied mit workspaceweiten Rechten, an das der API-Schlüssel gebunden werden kann.',
      'no-permissions': 'Wähle mindestens eine Berechtigung für diesen API-Schlüssel.',
      'permission-not-grantable':
        'Mindestens eine der gewählten Berechtigungen kann nicht über einen API-Schlüssel vergeben werden. Entferne sie aus der Auswahl.',
      'permission-not-held':
        'Dazu fehlt dir die Berechtigung: Du kannst einem API-Schlüssel nur Berechtigungen geben, die du selbst hast.',
    },
    // Verbundene Clients (IMM-601.8.2): der Zugriff, den ein MCP-Client per
    // OAuth hält. Sichtbar, wenn ein zweiter Tab denselben Zugriff bereits
    // entfernt hat.
    'connected-client': {
      'not-found': 'Dieser verbundene Client existiert nicht mehr.',
      'permission-not-held':
        'Dazu fehlt dir die Berechtigung: Du kannst einem verbundenen Client nur Berechtigungen geben, die du selbst hast.',
      'no-permissions': 'Wähle mindestens eine Berechtigung aus – oder erlaube alle Werkzeuge.',
    },
    // Übergang in eine verbundene Anwendung (IMM-751.2.1). Das Fenster steht im
    // Satz, nicht nur in der Konstante: ändert sich HANDOFF_MINT_WINDOW_MS,
    // muss diese Zeile mitwandern.
    'app-handoff': {
      'target-not-approved': 'Diese Anwendung ist für deinen Workspace nicht freigegeben.',
      'rate-limited': 'Zu viele Anfragen. Versuch es in einer Minute noch einmal.',
      'token-invalid':
        'Dieser Link wurde bereits verwendet oder ist abgelaufen. Öffne die Anwendung noch einmal aus immoOS heraus.',
    },
    // Die Zustimmungsseite (IMM-601.8.2): der Client hat uns ohne verwertbare
    // Anfrage hierher geschickt. Die Wortwahl schiebt den Fehler bewusst nicht
    // dem Mitglied zu — es hat den Ablauf nicht gestartet.
    'oauth-consent': {
      'invalid-request': 'Diese Verbindungsanfrage ist unvollständig. Starte sie in deinem Client noch einmal.',
    },
    'api-call-log': {
      'not-found': 'Dieser API-Aufruf wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      // Kein Mitglied sieht diesen Code: er tritt nur bei einer verletzten Invariante auf.
      'rejection-reason-without-auth-failure':
        'Ein Ablehnungsgrund kann nur zu einem API-Aufruf gespeichert werden, dessen Anmeldung fehlgeschlagen ist.',
    },
    company: {
      'not-found':
        'Diese Unternehmensdaten wurden nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'document-template': {
      'data-field-not-allowed':
        'Diese Vorlage enthält Datenfelder, die es für ihre Kategorie nicht gibt. Entferne sie und speichere erneut.',
      'name-taken': 'Eine Vorlage mit diesem Namen existiert bereits in dieser Kategorie.',
      'not-found': 'Diese Vorlage gibt es nicht mehr. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'email-template': {
      'name-taken': 'Es gibt schon eine E-Mail-Vorlage mit diesem Namen.',
      'subject-required': 'Bitte gib einen Betreff an.',
      'subject-too-long': 'Der Betreff darf höchstens 200 Zeichen lang sein.',
      'body-required': 'Bitte gib einen Text an.',
      'body-too-long': 'Der Text darf höchstens 20.000 Zeichen lang sein.',
      'fill-field-label-invalid': 'Die Bezeichnung eines Ausfüllfelds muss 1 bis 40 Zeichen lang sein.',
      'placeholder-unknown':
        'Diese E-Mail-Vorlage enthält einen Platzhalter, den es nicht gibt. Entferne ihn und speichere erneut.',
      'not-found': 'Diese E-Mail-Vorlage gibt es nicht mehr. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'company-onboarding': {
      'incomplete-required':
        'Im Unternehmens-Onboarding fehlen noch Pflichtangaben. Ergänze sie, um das Onboarding abzuschließen.',
      'invalid-transition':
        'Das Unternehmens-Onboarding ist bereits abgeschlossen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'not-found':
        'Für diesen Workspace wurde kein Unternehmens-Onboarding gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'member-onboarding': {
      'not-found':
        'Für dieses Mitglied wurde kein Onboarding gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'incomplete-required-core':
        'Für dein Profil fehlen noch Pflichtangaben: deine Position oder deine Einwilligung. Ergänze sie, um das Onboarding abzuschließen.',
      'invalid-transition':
        'Dein Onboarding lässt sich in seinem aktuellen Status nicht mehr ändern. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'organization-onboarding': {
      'not-found':
        'Dieses Standort-Onboarding wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'already-decided':
        'Dieses Standort-Onboarding ist schon über die Einladung hinaus. Eine neue Einladung ist nicht mehr möglich — bearbeite es in der Prüfung.',
      'cannot-exchange-session':
        'Dieser Onboarding-Link ist ungültig oder abgelaufen. Fordere bei deinem Ansprechpartner einen neuen Link an.',
      'consent-requires-granting-name':
        'Für eine erteilte Einwilligung fehlt der Name der Person, die sie erteilt hat. Trage den Namen ein.',
      'invalid-code': 'Dieser Bestätigungscode ist falsch oder abgelaufen. Prüfe den Code oder fordere einen neuen an.',
      'invalid-transition':
        'Dieses Standort-Onboarding lässt sich in seinem aktuellen Status nicht mehr ändern. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'missing-location-company':
        'Diesem Standort sind keine Unternehmensdaten zugeordnet, deshalb lässt sich das Onboarding nicht freigeben. Bitte wende dich an den immoOS-Support.',
      'submission-incomplete':
        'Im Standort-Onboarding fehlen noch Pflichtangaben oder Einwilligungen. Ergänze sie, um das Onboarding abzusenden.',
    },
    role: {
      'cannot-change-more-privileged-member':
        'Dazu fehlt dir die Berechtigung: Dieses Mitglied hat mehr Rechte als du, deshalb kannst du seine Rollen nicht ändern.',
      'cannot-change-own-role':
        'Du kannst deine eigene Rolle nicht ändern. Das kann nur ein anderes Mitglied mit der nötigen Berechtigung.',
      'cannot-grant-beyond-own-permissions':
        'Dazu fehlt dir die Berechtigung: Du kannst nur Rollen vergeben, deren Rechte du selbst hast.',
      'cannot-grant-global-access':
        'Dazu fehlt dir die Berechtigung: Du kannst keine workspaceweiten Rechte vergeben, die du selbst nicht hast.',
    },
    'workspace-member': {
      // Identical to `organization.not-found` on purpose: the gate collapses this refusal so Standort ids cannot be probed.
      'no-org-standing': 'Diese Organisation wurde nicht gefunden.',
    },
    'company-signature': {
      'signature-too-long': 'Die Signatur ist zu lang. Kürze ihren sichtbaren Text.',
      'unsupported-variable':
        'Die Signatur enthält eine Variable, die nicht unterstützt wird. Verwende nur die angebotenen Profil-Variablen.',
    },
    impersonation: {
      'handoff-not-redeemable':
        'Dieser Link ist nicht mehr gültig. Bitte starte eine neue Sitzung in der Admin-Konsole.',
      'step-up-failed': 'Bestätige die Impersonation mit einem aktuellen Code aus deiner Authenticator-App.',
      'target-not-impersonatable': 'Plattformpersonal und Admin-Konten können nicht impersonifiziert werden.',
      'target-not-member': 'Dieses Mitglied gehört nicht zu dem Workspace, in dem du die Impersonation starten willst.',
    },
    'member-invite': {
      'cannot-exchange-session':
        'Dieser Einladungslink ist ungültig oder abgelaufen. Bitte die Person, die dich eingeladen hat, um eine neue Einladung.',
      'invalid-code': 'Dieser Bestätigungscode ist falsch oder abgelaufen. Prüfe den Code oder fordere einen neuen an.',
    },
    'workspace-branding': {
      'invalid-logo':
        'Diese Datei kann nicht als Logo oder Favicon verwendet werden. Wähle ein Bild im erlaubten Format und in der erlaubten Größe.',
    },
  },
  // Erster registrierter `communications.*`-Code (IMM-530.2). Der Direktpfad
  // ist heute nur für Google verdrahtet; das Wire-Schema akzeptiert weiterhin
  // MICROSOFT/IMAP, deshalb erklärt diese Meldung die Einschränkung.
  communications: {
    mailbox: {
      'provider-not-supported':
        'Aktuell kann nur ein Gmail-Postfach verbunden werden. Microsoft 365, Exchange und IMAP werden noch nicht unterstützt.',
      'attachments-too-large':
        'Die Anhänge sind zusammen zu groß für eine E-Mail. Entferne Anhänge oder verkleinere sie, bevor du sendest.',
      'grant-mismatch':
        'Das gewählte Absenderkonto ist nicht dein verbundenes Postfach, deshalb wurde nichts gesendet. Lade die Seite neu, um dein aktuelles Postfach als Absender zu sehen.',
      'grant-not-active':
        'Dieses Postfach ist nicht verbunden. Es muss zuerst in den Postfach-Einstellungen verbunden werden.',
      'personal-link-unavailable':
        'Für diesen Interessenten gibt es keine persönliche Seite, deshalb wurde nichts gesendet. Ersetze den Link zur persönlichen Seite oder entferne ihn.',
      'invalid-cursor': 'Die Postfachliste ist nicht mehr aktuell. Lade die Seite neu, um weitere E-Mails zu sehen.',
      'oauth-state-mismatch':
        'Die Anfrage zur Postfach-Verbindung ist abgelaufen oder ungültig. Starte die Verbindung in den Postfach-Einstellungen neu.',
      'send-refused': 'Dein Anbieter hat die E-Mail nicht angenommen: {reason}. Es wurde nichts gesendet.',
      'connected-as-company-mailbox':
        'Diese Adresse ist bereits als Firmenpostfach verbunden und kann nicht zusätzlich als persönliches Postfach verbunden werden.',
    },
    'imap-mailbox': {
      'login-refused': 'Anmeldung fehlgeschlagen. Bitte prüfe E-Mail-Adresse und Passwort.',
      'server-unreachable': 'Der Server {host} antwortet nicht. Bitte prüfe Server-Adresse und Port.',
      'sending-refused': 'Empfangen klappt, Senden nicht. Bitte prüfe den Postausgangsserver (SMTP).',
      unencrypted:
        'immoOS verbindet sich nur verschlüsselt. Dein Server bietet keine verschlüsselte Verbindung an. Nutze Port 993 (IMAP) und 465 (SMTP) oder frag deinen Anbieter.',
      'too-many-attempts':
        'Zu viele Verbindungsversuche in kurzer Zeit. Bitte versuche es in {retryInMinutes, plural, one {einer Minute} other {# Minuten}} erneut.',
    },
    'company-mailbox': {
      'already-connected': 'Dieses Postfach ist bereits als Firmenpostfach verbunden.',
      'connected-as-personal':
        'Diese Adresse ist bereits als persönliches Postfach eines Mitglieds verbunden. Die persönliche Verbindung muss zuerst getrennt werden.',
      'not-found':
        'Dieses Firmenpostfach ist nicht mehr verbunden. Lade die Seite neu, um die aktuellen Firmenpostfächer zu sehen.',
      'reconnect-address-mismatch': 'Melde dich mit {email} an, um dieses Firmenpostfach erneut zu verbinden.',
    },
    appointment: {
      'not-found': 'Dieser Termin existiert nicht mehr.',
      'title-required': 'Bitte gib einen Titel an.',
      'start-required': 'Bitte gib einen Beginn an.',
      'end-before-start': 'Das Ende muss nach dem Beginn liegen.',
      'calendar-not-connected': 'Verbinde zuerst deinen Kalender, um Termine anzulegen.',
      'provider-write-failed': 'Die Änderung konnte nicht gespeichert werden. Versuch es noch einmal.',
      'link-not-in-workspace': 'Dieser Datensatz gehört nicht zu deinem Workspace und kann nicht verknüpft werden.',
      'conference-not-supported':
        'Diese Kalenderverbindung kann keine Videokonferenz erstellen. Du kannst einen Einwahllink von Hand einfügen.',
      'imap-no-calendar':
        'Dein Postfach ist über IMAP verbunden und hat keinen Kalender. Termine kannst du deshalb noch nicht anlegen.',
    },
    'viewing-invitation': {
      'cannot-create-contact-without-email': 'Hinterlege zuerst eine E-Mail-Adresse beim Kontakt, um ihn einzuladen.',
    },
    'whatsapp-message': {
      'invalid-recipient-number': 'Die hinterlegte Telefonnummer ist keine gültige Nummer.',
      'provider-unavailable': 'WhatsApp ist momentan nicht erreichbar. Bitte versuche es später noch einmal.',
    },
    'whatsapp-link': {
      'already-connected':
        'Deine WhatsApp-Nummer ist schon verbunden. Trenne sie zuerst, wenn du eine andere Nummer verbinden möchtest.',
      'channel-unavailable': 'WhatsApp ist für immoOS gerade nicht eingerichtet. Versuch es später noch einmal.',
    },
    'messenger-chat': {
      'not-found': 'Dieser Chat wurde nicht gefunden. Lade die Liste neu, um den aktuellen Stand zu sehen.',
    },
    'telegram-link': {
      'already-connected':
        'Dein Telegram-Konto ist schon verbunden. Trenne es zuerst, wenn du ein anderes Konto verbinden möchtest.',
      'channel-unavailable': 'Telegram ist für immoOS gerade nicht eingerichtet. Versuch es später noch einmal.',
    },
    'email-message': {
      'not-found': 'Diese E-Mail wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'email-thread': {
      'contact-not-found': 'Dieser Kontakt wurde nicht gefunden. Wähle einen anderen Kontakt für die Zuordnung aus.',
      'not-found': 'Diese Konversation wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'placement-forbidden': 'Du darfst diesem Datensatz keine E-Mails zuordnen. Lade die Seite neu.',
      'subject-not-found': 'Dieser Datensatz existiert nicht mehr.',
    },
    'mailbox-grant': {
      'not-found':
        'Das Postfach, zu dem diese E-Mail gehört, wurde getrennt. Die E-Mail lässt sich erst wieder öffnen, wenn das Postfach neu verbunden ist.',
    },
    // Kein Mitglied sieht diese Codes: sie stammen aus dem Nachrichtenversand im Hintergrund.
    message: {
      'no-channel-sender':
        'Für diesen Nachrichtenkanal ist kein Versand eingerichtet. Die Nachricht wurde nicht gesendet.',
      'no-renderer': 'Für diese Nachrichtenart gibt es keine Vorlage. Die Nachricht wurde nicht gesendet.',
      'provider-failed': 'Der Versanddienst hat die Nachricht nicht angenommen. Sie wurde nicht gesendet.',
      'recipients-failed': 'Die Nachricht ist nicht bei allen Empfängern angekommen.',
      'renderer-kind-not-supported':
        'Dieser Versandweg unterstützt diese Nachrichtenform noch nicht. Die Nachricht wurde nicht gesendet.',
    },
  },
  admin: {
    auth: {
      'user-not-an-admin':
        'Diese Oberfläche ist nur für Plattformpersonal. Bitte nutzen Sie app.immoos.ai für Ihre Workspace.',
    },
    skill: {
      'not-found': 'Für diesen Dokumenttyp gibt es keinen Skill.',
      'not-configured': 'Dieser Skill ist noch nicht konfiguriert.',
      'stale-edit': 'Dieser Skill wurde zwischenzeitlich geändert. Bitte lade die Seite neu, bevor du speicherst.',
      'prompt-empty': 'Der Prompt darf nicht leer sein.',
      'prompt-too-long': 'Der Prompt ist zu lang (maximal 10.000 Zeichen).',
      'status-unchanged': 'Der Skill hat bereits den gewünschten Status.',
      'cannot-deactivate-system-skill': 'Der Fallback-Skill kann nicht deaktiviert werden.',
      'system-not-found': 'Der Fallback-Skill konnte nicht geladen werden. Bitte versuche es erneut.',
    },
    landiq: {
      unreachable: 'LandIQ ist gerade nicht erreichbar. Versuch es in ein paar Minuten noch einmal.',
      'credential-rejected': 'immoOS darf LandIQ nicht abfragen – der Service-Token ist falsch oder abgelaufen.',
      'not-configured': 'LandIQ ist für diese Umgebung nicht eingerichtet – der Service-Token fehlt.',
    },
    'landiq-data-run': {
      'not-found': 'Diesen Datenlauf gibt es in LandIQ nicht mehr.',
      'log-cursor-invalid': 'Der Log-Stand ist nicht mehr gültig. Lade die Seite neu.',
    },
    'sandbox-reset': {
      'not-configured': 'Das Zurücksetzen der Sandbox ist in dieser Umgebung nicht eingerichtet.',
      'github-unreachable': 'GitHub ist gerade nicht erreichbar. Versuch es gleich noch mal.',
    },
    'agent-skill': {
      'not-found': 'Diesen Agent-Skill gibt es nicht.',
      'key-conflict': 'Diesen Schlüssel gibt es bereits. Bitte wähle einen anderen.',
      'key-invalid':
        'Der Schlüssel darf nur Kleinbuchstaben, Ziffern, Bindestriche und Unterstriche enthalten (höchstens 60 Zeichen).',
      'stale-edit':
        'Dieser Agent-Skill wurde zwischenzeitlich geändert. Bitte lade die Seite neu, bevor du speicherst.',
      'status-unchanged': 'Der Agent-Skill hat bereits den gewünschten Status.',
      'instructions-empty': 'Die Anweisungen dürfen nicht leer sein.',
      'instructions-too-long': 'Die Anweisungen sind zu lang (maximal 10.000 Zeichen).',
      'name-invalid': 'Der Name muss zwischen 1 und 60 Zeichen lang sein.',
      'description-invalid': 'Die Beschreibung muss zwischen 1 und 500 Zeichen lang sein.',
      'triggers-invalid': 'Bitte wähle mindestens einen gültigen Auslöser aus.',
      'unknown-tool': 'Mindestens ein ausgewähltes Werkzeug gibt es nicht.',
      'approval-window-invalid': 'Das Freigabefenster muss zwischen 1 und 7 Tagen liegen.',
    },
    'push-event': {
      'not-offered': 'Diese Benachrichtigung steht dir nicht zur Verfügung.',
    },
    admin: {
      'cannot-deactivate-self': 'Du kannst dein eigenes Admin-Konto nicht deaktivieren.',
      'duplicate-email': 'Diese E-Mail-Adresse ist bereits als Admin hinterlegt.',
      'last-active-admin': 'Es muss immer mindestens ein aktiver Admin verbleiben.',
    },
  },
  assistant: {
    'skill-instance': {
      'not-found': 'Diesen Skill gibt es in diesem Workspace nicht.',
      'definition-not-found': 'Diesen Skill gibt es nicht mehr.',
      'definition-inactive': 'Dieser Skill ist derzeit nicht verfügbar.',
      'config-invalid': 'Diese Konfiguration ist ungültig. Die bisherige Einstellung bleibt bestehen.',
      'already-exists': 'Du hast diesen Skill bereits eingerichtet.',
      revoked: 'Dieser Zugriff wurde widerrufen und kann nicht mehr geändert werden.',
      'stale-edit': 'Dieser Skill wurde zwischenzeitlich geändert. Bitte lade die Seite neu, bevor du speicherst.',
      'not-runnable': 'Dieser Skill kann gerade nicht ausgeführt werden.',
    },
    'skill-run': {
      'not-found': 'Diesen Lauf gibt es nicht.',
    },
    'proposed-action': {
      'not-found': 'Diese Freigabe gibt es nicht.',
      'instance-not-runnable':
        'Dieser Skill ist gerade pausiert — die Aktion kann erst nach der Reaktivierung freigegeben werden.',
      'scope-lost': 'Dein Zugriff reicht für diese Aktion nicht mehr aus. Sie wurde nicht ausgeführt.',
    },
    'capability-gap': {
      'invalid-category': 'Diese nicht abgedeckte Anfrage hat eine unbekannte Kategorie und wurde nicht erfasst.',
      'invalid-label':
        'Die Bezeichnung einer nicht abgedeckten Anfrage muss zwischen {minLength, number} und {maxLength, number} Zeichen lang sein. Die Anfrage wurde nicht erfasst.',
      'invalid-raw-request':
        'Der Wortlaut einer nicht abgedeckten Anfrage muss zwischen {minLength, number} und {maxLength, number} Zeichen lang sein. Die Anfrage wurde nicht erfasst.',
    },
    conversation: {
      'not-found': 'Diese Unterhaltung wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    llm: {
      'content-not-supported':
        'Dieser Anhang kann vom KI-Modell nicht gelesen werden. Wähle ein Modell, das Dokumente und Bilder unterstützt, oder ein anderes Dateiformat.',
    },
    message: {
      'content-too-long':
        'Deine Nachricht darf höchstens {maxLength, number} Zeichen lang sein. Kürze sie, bevor du sie sendest.',
      'empty-content': 'Deine Nachricht ist leer. Schreib etwas, bevor du sie sendest.',
    },
    model: {
      'not-allowed': 'Dieses Modell steht für den Chat nicht zur Verfügung. Wähle ein anderes Modell.',
    },
    'page-read': {
      'not-pending': 'Die Seite wird nicht mehr gebraucht. Stell deine Frage bei Bedarf noch einmal.',
    },
    run: {
      'not-found': 'Dieser Assistenten-Lauf wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    tool: {
      'invalid-arguments': 'Ein Werkzeug des Assistenten wurde mit ungültigen Angaben aufgerufen und nicht ausgeführt.',
    },
    'whatsapp-pending-send': {
      'not-found': 'Dieser Entwurf existiert nicht mehr oder wurde noch nicht bestätigt.',
    },
  },
  valuation: {
    'valuation-report': {
      'not-found': 'Dieses Wertgutachten wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'valuation-agent-request': {
      'not-found': 'Diesen Auftrag gibt es nicht.',
      'not-open': 'Dieser Auftrag ist nicht mehr offen.',
      'already-answered': 'Dieser Auftrag ist bereits beantwortet.',
      'no-matching-request': 'Zu dieser Lieferung gibt es keinen passenden Auftrag.',
    },
    'valuation-agent-result': {
      'agent-only': 'Briefings und Bewertungen liefert nur der Bewertungs-Agent.',
      'not-a-result-upload':
        'Diese Datei wurde nicht als Ergebnis zu diesem Auftrag hochgeladen. Lade das PDF zum Auftrag hoch und bestätige den Upload.',
      'property-unavailable': 'Das Objekt zu diesem Auftrag gibt es nicht mehr.',
    },
    'acquisition-briefing': {
      'phase-too-early': 'Ein Akquisebriefing kannst du erst ab der Phase „Termin vereinbart“ erstellen.',
      'object-type-not-supported': 'Für diese Objektart gibt es noch kein Akquisebriefing.',
      'address-incomplete': 'Für ein Briefing braucht das Objekt eine vollständige Adresse.',
      'in-progress':
        'Für dieses Objekt wird gerade ein Akquisebriefing erstellt. Lade die Seite neu, um den Stand zu sehen.',
    },
    'desk-valuation': {
      'object-type-not-supported': 'Für diese Objektart gibt es noch keine Bewertung 1a.',
      'address-incomplete': 'Für eine Bewertung braucht das Objekt eine vollständige Adresse.',
      'in-progress':
        'Für dieses Objekt wird gerade eine Bewertung 1a erstellt. Lade die Seite neu, um den Stand zu sehen.',
    },
    'valuation-request-document': {
      'kind-mismatch': 'Diese Datei wurde nicht für diesen Anhangsbereich hochgeladen. Bitte lade sie erneut hoch.',
      'property-unavailable':
        'Das Objekt zu dieser Bewertungsanfrage ist nicht mehr verfügbar. Die Datei konnte nicht angehängt werden.',
      'not-found':
        'Dieses Dokument der Bewertungsanfrage wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'valuation-request': {
      'cannot-submit-for-approval': 'Diese Bewertung kann nicht zur Freigabe eingereicht werden.',
      // One code per REFUSAL, not per action: the three `cannot-*` codes are the
      // lifecycle refusal ("not awaiting release") for their action, and the two
      // below are the validation refusals. A single code per action would render
      // one of its two refusals with the other's message.
      'cannot-release': 'Diese Bewertung wartet nicht auf Freigabe.',
      'cannot-return': 'Diese Bewertung wartet nicht auf Freigabe.',
      'not-claimed': 'Diese Anfrage ist derzeit nicht übernommen.',
      'cannot-adjust-result': 'Diese Bewertung wartet nicht auf Freigabe.',
      'result-invalid': 'Die Preisspanne ist ungültig: Der Startpreis muss innerhalb der Spanne liegen.',
      'return-reason-required': 'Bitte gib einen Grund für die Rückgabe an.',
      'already-taken':
        'Diese Anfrage wurde bereits von einer anderen Person übernommen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-modify':
        'Diese Bewertungsanfrage kann nicht mehr geändert oder gelöscht werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-submit':
        'Diese Bewertungsanfrage ist kein Entwurf mehr und kann nicht erneut eingereicht werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-take-on':
        'Diese Anfrage kann in ihrem aktuellen Status nicht übernommen werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-withdraw':
        'Diese Anfrage kann nicht mehr zurückgezogen werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      incomplete:
        'In dieser Bewertungsanfrage fehlen noch Pflichtangaben. Ergänze sie im Formular, bevor du einreichst.',
      'not-found': 'Diese Bewertungsanfrage wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'open-request-exists':
        'Für diesen Deal läuft bereits eine Bewertungsanfrage. Lade die Seite neu, um sie zu sehen.',
    },
  },
  activity: {
    'activity-record': {
      'empty-content': 'Bitte gib einen Inhalt ein.',
      'content-too-long': 'Der Inhalt darf höchstens {maxLength} Zeichen lang sein.',
      'future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'missing-due-at': 'Bitte gib ein „Fällig am"-Datum an.',
      'past-due-at': '„Fällig am" muss in der Zukunft liegen.',
      'timestamp-not-allowed-for-mode':
        'Eine festgehaltene Aktivität hat kein „Fällig am", eine geplante kein „Stattgefunden am".',
      'outcome-not-allowed-for-mode': 'Eine geplante Aktivität hat noch kein Ergebnis.',
      'communication-attributes-required': 'Bitte wähle Art und Richtung der Kommunikation.',
      'communication-attributes-not-allowed': 'Nur eine Kommunikation hat Art und Richtung.',
      'appointment-type-required': 'Bitte wähle eine Termin-Art.',
      'appointment-type-not-allowed': 'Nur ein Termin hat eine Termin-Art.',
      'appointment-type-not-for-subject': 'Diese Termin-Art gibt es hier nicht. Bitte wähle eine aus der Liste.',
      'subject-not-supported': 'Für diesen Datensatz können noch keine Aktivitäten erfasst werden.',
      'mode-not-supported': 'Aktivitäten können hier noch nicht geplant werden.',
      'kind-not-supported': 'Diese Aktivitätsart ist hier noch nicht verfügbar.',
      'subject-too-long': 'Der Betreff darf höchstens {maxLength} Zeichen lang sein.',
      'duration-out-of-range': 'Bitte gib eine gültige Dauer in Minuten an.',
      'outcome-type-mismatch':
        '{type, select, EMAIL {Dieses Ergebnis passt nicht zu einer E-Mail.} other {Dieses Ergebnis passt nicht zu dieser Aktivität.}}',
      'reschedule-not-open':
        '{state, select, DONE {Eine erledigte Aktivität kann nicht neu terminiert werden.} CANCELLED {Eine abgesagte Aktivität kann nicht neu terminiert werden.} other {Nur eine offene Aktivität kann neu terminiert werden.}}',
      'reschedule-past-due-at': 'Der neue Termin ({dueAt}) liegt in der Vergangenheit. Wähle einen zukünftigen Termin.',
      'complete-not-open': 'Diese Aktivität ist nicht mehr offen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cancel-not-open':
        'Diese Aktivität ist nicht mehr offen und kann nicht abgesagt werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reassign-not-open':
        'Diese Aktivität ist nicht mehr offen, die Zuständigkeit kann nicht mehr geändert werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'email-activity-not-editable': 'E-Mail-Aktivitäten kannst du nicht bearbeiten.',
      'delete-forbidden': 'Du darfst diese Aktivität nicht löschen.',
      'delete-not-supported': 'Diese Aktivität kann hier nicht gelöscht werden.',
      'complete-future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'outcome-kind-mismatch': 'Dieses Ergebnis passt nicht zu dieser Aktivitätsart.',
      'outcome-on-note': 'Eine Notiz hat kein Ergebnis.',
      'follow-up-not-applicable':
        'Für diese Aktivität wird keine Folgeaktivität angelegt. Entferne das Fälligkeitsdatum der Folgeaktivität.',
      'past-follow-up-due-at': 'Die Folgeaktivität muss in der Zukunft fällig sein.',
      'not-found': 'Diese Aktivität wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    activity: {
      'not-found': 'Diese Aktivität wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    cursor: { invalid: 'Der Verlauf ist nicht mehr aktuell. Lade die Seite neu, um weitere Einträge zu sehen.' },
  },
  lead: {
    lead: {
      'complete-activity-outcome-type-mismatch':
        '{type, select, EMAIL {Dieses Ergebnis passt nicht zu einer E-Mail.} other {Dieses Ergebnis passt nicht zu dieser Aktivität.}}',
      'communication-outcome-type-mismatch':
        '{type, select, EMAIL {Dieses Ergebnis passt nicht zu einer E-Mail.} other {Dieses Ergebnis passt nicht zu dieser Aktivität.}}',
      'marketing-type-required': 'Wähle aus, ob das Objekt verkauft oder vermietet wird.',
      'complete-activity-not-open':
        'Diese Aktivität ist nicht mehr offen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reschedule-activity-not-open':
        '{state, select, DONE {Eine erledigte Aktivität kann nicht neu terminiert werden.} CANCELLED {Eine abgesagte Aktivität kann nicht neu terminiert werden.} other {Nur eine offene Aktivität kann neu terminiert werden.}}',
      'cancel-activity-not-open':
        'Diese Aktivität ist nicht mehr offen und kann nicht abgesagt werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reassign-activity-not-open':
        'Diese Aktivität ist nicht mehr offen, die Zuständigkeit kann nicht mehr geändert werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'complete-activity-future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'reschedule-activity-past-due-at':
        'Der neue Termin ({dueAt}) liegt in der Vergangenheit. Wähle einen zukünftigen Termin.',
      'complete-activity-outcome-on-note': 'Eine Notiz hat kein Ergebnis.',
      'complete-activity-follow-up-not-applicable':
        'Für diese Aktivität wird keine Folgeaktivität angelegt. Entferne das Fälligkeitsdatum der Folgeaktivität.',
      'complete-activity-past-follow-up-due-at': 'Die Folgeaktivität muss in der Zukunft fällig sein.',
      'activity-not-found':
        'Diese Aktivität wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'communication-empty-content': 'Bitte gib einen Inhalt ein.',
      'note-empty-body': 'Bitte gib einen Inhalt ein.',
      'communication-content-too-long': 'Der Inhalt darf höchstens {maxLength} Zeichen lang sein.',
      'note-body-too-long': 'Der Inhalt darf höchstens {maxLength} Zeichen lang sein.',
      'communication-subject-too-long': 'Der Betreff darf höchstens {maxLength} Zeichen lang sein.',
      'communication-duration-out-of-range': 'Bitte gib eine gültige Dauer in Minuten an.',
      'communication-future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'note-future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'communication-missing-due-at': 'Bitte gib ein „Fällig am"-Datum an.',
      'note-missing-due-at': 'Bitte gib ein „Fällig am"-Datum an.',
      'communication-past-due-at': '„Fällig am" muss in der Zukunft liegen.',
      'note-past-due-at': '„Fällig am" muss in der Zukunft liegen.',
      'activity-assignee-not-assignable':
        'Dieses Mitglied kann für diese Aktivität nicht zuständig sein. Wähle ein anderes Mitglied.',
      'reassign-activity-assignee-not-assignable':
        'Dieses Mitglied kann für diese Aktivität nicht zuständig sein. Wähle ein anderes Mitglied.',
      'assignment-denied':
        'Dazu fehlt dir die Berechtigung: Du kannst diesen Lead nur Mitgliedern deines Standorts zuweisen.',
      'mutation-denied': 'Dazu fehlt dir die Berechtigung: Du kannst diesen Lead ansehen, aber nicht ändern.',
      'converted-terminal':
        'Dieser Lead wurde bereits konvertiert und kann nicht mehr geändert werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'invalid-state-transition':
        'Diese Aktion ist im aktuellen Status des Leads nicht möglich. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'not-found': 'Dieser Lead wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'invalid-received-at': '„Empfangen am" muss zwischen heute und vor 90 Tagen liegen.',
      'last-name-required': 'Bitte gib einen Nachnamen ein.',
      'loss-reason-required': 'Bitte wähle einen Verlustgrund.',
      'loss-note-too-long': 'Die Verlust-Notiz ist zu lang. Kürze sie, bevor du den Lead als verloren markierst.',
      'organisation-not-found': 'Dieser Standort gehört nicht zu diesem Workspace. Wähle einen anderen Standort.',
      'reassign-target-organisation-not-found':
        'Der gewählte Standort wurde nicht gefunden. Lade die Seite neu und wähle einen anderen Standort.',
      'partner-required': 'Bei Quelle „Partner" ist ein Lead-Partner erforderlich.',
      'partner-inactive': 'Dieser Lead-Partner ist deaktiviert. Wähle einen aktiven Lead-Partner.',
      'owner-first-name-required': 'Trage den Vornamen des Eigentümers ein.',
    },
    'lead-partner': {
      'invalid-name': 'Bitte gib einen gültigen Namen für den Lead-Partner ein.',
      'name-taken': 'Es gibt bereits einen Lead-Partner mit diesem Namen. Wähle einen anderen Namen.',
      'key-already-issued':
        'Dieser Lead-Partner hat bereits einen API-Schlüssel. Widerrufe ihn, bevor du einen neuen erstellst.',
      'not-found': 'Dieser Lead-Partner wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'lead-push-replay': {
      'not-replayable': 'Diese Anfrage kann nicht erneut übermittelt werden.',
    },
  },
  deal: {
    deal: {
      'assignment-denied':
        'Dazu fehlt dir die Berechtigung: Du kannst diesen Deal nur Mitgliedern deines Standorts zuweisen.',
      'cannot-add-contact-active-deal-overlap':
        'Dieser Kontakt gehört bereits zu einem anderen aktiven Deal auf diesem Objekt.',
      'cannot-add-contact-duplicate': 'Dieser Kontakt ist bereits mit dem Deal verknüpft.',
      'cannot-add-contact-non-owner-link-conflict':
        'Dieser Kontakt ist bereits mit einer anderen Rolle mit dem Objekt verknüpft. Ein Deal setzt eine Eigentümer-Beziehung voraus.',
      'cannot-add-contact-not-owner':
        'Dieser Kontakt ist kein Eigentümer des Objekts. Füge ihn zuerst als Eigentümer am Objekt hinzu.',
      'cannot-advance-phase-not-active':
        'Dieser Deal ist nicht aktiv und kann nicht in die nächste Phase wechseln. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-advance-phase-skip':
        'Phasen können nicht übersprungen werden: Ein Deal wechselt immer nur in die direkt folgende Phase. Lade die Seite neu, um die aktuelle Phase zu sehen.',
      'cannot-change-phase-expectation-mismatch':
        'Die Phase dieses Deals hat sich inzwischen geändert. Lade die Seite neu, um die aktuelle Phase zu sehen.',
      'cannot-change-phase-foreign-evidence':
        'Die angegebenen Belege lassen sich diesem Deal nicht zuordnen. Verschiebe den Deal ohne Belege oder nenne nur Aktivitäten dieses Deals.',
      'cannot-create-active-deal-exists': 'Für dieses Objekt existiert bereits ein aktiver Deal dieser Geschäftsart.',
      'cannot-create-commission-out-of-range': 'Die Provision muss zwischen 0 und 100 liegen.',
      'cannot-create-duplicate-contact': 'Derselbe Kontakt ist mehrfach ausgewählt. Entferne den doppelten Eintrag.',
      'cannot-create-invalid-initial-phase':
        'Ein neuer Deal kann nur in der Phase „Im Austausch" oder „Termin vereinbart" beginnen.',
      'cannot-create-invalid-source':
        'Die Quelle passt nicht zum Deal: Ein Deal aus einem Lead braucht diesen Lead, ein manuell angelegter Deal darf keinen haben.',
      'cannot-create-negative-expected-sale-price': 'Der erwartete Verkaufspreis darf nicht negativ sein.',
      'cannot-create-no-contact': 'Bitte wähle mindestens einen Eigentümer-Kontakt.',
      'cannot-create-non-owner-link-conflict':
        'Ein gewählter Kontakt ist bereits mit einer anderen Rolle mit dem Objekt verknüpft. Ein Deal setzt eine Eigentümer-Beziehung voraus.',
      'cannot-create-property-archived':
        'Dieses Objekt ist archiviert. Hebe zuerst die Archivierung auf, um einen Deal anzulegen.',
      'cannot-create-type-mismatch': 'Die Geschäftsart passt nicht zur Vermarktungsart des gewählten Objekts.',
      'cannot-defer-not-active':
        'Ein gewonnener oder verlorener Deal kann nicht zurückgestellt werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-defer-past-date': 'Das Wiedervorlage-Datum muss heute oder in der Zukunft liegen.',
      'cannot-delete-already-deleted':
        'Dieser Deal wurde bereits gelöscht. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-delete-won-immutable':
        'Dieser Deal ist gewonnen und lässt sich nicht mehr löschen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-manage-contacts-won-immutable':
        'Dieser Deal ist gewonnen, seine Kontakte lassen sich nicht mehr ändern. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-mark-lost-already-lost':
        'Dieser Deal ist bereits verloren. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-mark-lost-won-immutable':
        'Dieser Deal ist gewonnen und kann nicht mehr als verloren markiert werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-remove-contact-last':
        'Mindestens ein Eigentümer-Kontakt ist erforderlich. Füge zuerst einen weiteren Eigentümer hinzu, bevor du diesen entfernst.',
      'cannot-remove-contact-not-linked':
        'Dieser Kontakt ist nicht mehr mit dem Deal verknüpft. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-reopen-not-lost':
        'Dieser Deal ist nicht verloren und kann nicht wieder geöffnet werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-restore-active-deal-exists':
        'Für dieses Objekt gibt es bereits einen aktiven Deal. Du kannst den gelöschten Deal erst wiederherstellen, wenn der andere abgeschlossen oder gelöscht wurde.',
      'cannot-restore-not-deleted':
        'Dieser Deal ist nicht gelöscht, es gibt nichts wiederherzustellen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-restore-property-archived':
        'Das Objekt dieses Deals ist archiviert. Hebe zuerst die Archivierung auf, um den Deal wiederherzustellen.',
      'cannot-resume-not-deferred':
        'Dieser Deal ist nicht zurückgestellt. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-revert-phase-missing-reason': 'Bitte begründe den Rückschritt.',
      'cannot-revert-phase-not-active':
        'Dieser Deal ist nicht aktiv und kann nicht in eine frühere Phase zurückgesetzt werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-revert-phase-not-earlier':
        'Ein Deal kann nur in eine frühere Phase zurückgesetzt werden. Lade die Seite neu, um die aktuelle Phase zu sehen.',
      'cannot-update-mandate-terms-not-sale': 'Auftragsdaten kannst du nur bei Verkaufs-Deals erfassen.',
      'cannot-update-mandate-terms-won-immutable':
        'Dieser Deal ist gewonnen. Seine Auftragsdaten kannst du nicht mehr ändern.',
      'cannot-update-notes-empty': 'Es gibt keine Änderung an den Notizen, die gespeichert werden könnte.',
      'cannot-update-notes-won-immutable':
        'Dieser Deal ist gewonnen, seine Notizen lassen sich nicht mehr ändern. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'not-found': 'Dieser Deal wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reassign-target-organization-not-found':
        'Der gewählte Standort wurde nicht gefunden. Lade die Seite neu und wähle einen anderen Standort.',
    },
    'generated-document': {
      'cannot-generate-not-active': 'Dokumente lassen sich nur für aktive Deals erstellen.',
      'cannot-generate-rental-deal': 'Für Vermietungs-Deals lassen sich keine Dokumente erstellen.',
      'cannot-generate-missing-fields':
        'Für dieses Dokument {count, plural, one {fehlt noch # Pflichtangabe} other {fehlen noch # Pflichtangaben}}. Ergänze sie und versuche es erneut.',
      'cannot-generate-preview-outdated':
        'Die Daten oder die Vorlage haben sich inzwischen geändert. Prüfe die Vorschau und erstelle das Dokument erneut.',
      'not-found': 'Dieses Dokument wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'manage-denied': 'Dazu fehlt dir die Berechtigung: Du kannst nur Dokumente löschen, die du selbst erstellt hast.',
      'cannot-delete-generating': 'Dieses Dokument wird gerade erstellt. Du kannst es löschen, sobald es fertig ist.',
      'cannot-retry-not-failed':
        'Dieses Dokument ist nicht fehlgeschlagen. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-download-not-ready': 'Dieses Dokument ist noch nicht fertig. Versuche es gleich noch einmal.',
      'cannot-delete-signing-open': 'Zieh zuerst die Unterschriftsanfrage zurück.',
      'cannot-delete-signed': 'Unterschriebene Dokumente können nicht gelöscht werden.',
      'cannot-mark-original-not-power-of-attorney': '„Original eingegangen“ gibt es nur bei einer Vollmacht.',
      'template-not-found': 'Diese Vorlage gibt es nicht mehr. Wähle eine andere Vorlage.',
    },
    inquiry: {
      'cannot-submit-required-fields-missing': 'Bitte fülle alle Pflichtfelder aus: {missingFields}.',
      'cannot-submit-email-invalid': 'Bitte gib eine gültige E-Mail-Adresse an.',
      'property-not-found': 'Dieses Objekt wurde nicht gefunden.',
    },
    'signing-request': {
      'cannot-send-not-released': 'Das Unterschreiben per Link ist noch nicht freigeschaltet.',
      'cannot-send-not-active': 'Zur Unterschrift senden geht nur bei aktiven Verkaufs-Deals.',
      'cannot-send-document-not-ready': 'Das Dokument ist noch nicht fertig erstellt.',
      'cannot-send-document-outdated':
        'Dieser Maklervertrag wurde vor der Online-Unterschrift erstellt. Erstelle ihn neu, um ihn zur Unterschrift zu senden.',
      'cannot-send-mandate-terms-missing':
        'Hinterlege Auftragsart und Provision Verkäufer im Auftrag und erstelle den Maklervertrag dann neu.',
      'cannot-send-already-requested': 'Für dieses Dokument läuft bereits eine Unterschriftsanfrage.',
      'cannot-send-owners-changed': 'Die Eigentümer des Deals haben sich geändert. Öffne den Dialog erneut.',
      'cannot-send-company-data-incomplete': 'Bitte einen Owner oder Admin, die Unternehmensdaten zu ergänzen.',
      'cannot-send-owner-email-missing':
        '{count, plural, one {Einem Eigentümer fehlt} other {# Eigentümern fehlt}} noch die E-Mail-Adresse.',
      'link-invalid': 'Dieser Link ist ungültig.',
      'cannot-sign-name-missing': 'Bitte geben Sie Ihren vollständigen Namen ein.',
      'cannot-sign-inactive':
        'Diese Unterschriftsanfrage ist nicht mehr aktiv. Bei Fragen wenden Sie sich an {companyName}.',
      'cannot-sign-page-outdated':
        'Diese Seite ist nicht mehr aktuell. Bitte laden Sie die Seite neu und unterschreiben Sie dann.',
      'not-found':
        'Diese Unterschriftsanfrage wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-remind-closed': 'Diese Unterschriftsanfrage ist abgeschlossen.',
      'cannot-remind-not-active': 'Erinnern geht nur bei aktiven Deals.',
      'cannot-withdraw-closed': 'Diese Unterschriftsanfrage ist abgeschlossen.',
      'cannot-withdraw-not-active': 'Zurückziehen geht nur bei aktiven Deals.',
      'cannot-mark-acceptance-reply-not-signed':
        'Die Annahme per E-Mail kannst du erst markieren, wenn der Eigentümer den Maklervertrag unterschrieben hat.',
      'cannot-revoke-not-signed': 'Für diesen Link gibt es keinen unterschriebenen Maklervertrag.',
      'cannot-revoke-period-ended':
        'Die Frist für einen Widerruf über diese Seite ist abgelaufen. Bei Fragen wenden Sie sich an {companyName}.',
    },
    'brokerage-agreement': {
      'cannot-send-notice-not-active': 'Nur für aktive Interessenten möglich.',
      'cannot-send-notice-already-concluded': 'Der Maklervertrag ist bereits abgeschlossen.',
      'cannot-send-notice-no-email':
        'Der Kontakt hat keine E-Mail-Adresse. Ergänze sie, um die Widerrufsbelehrung zu senden.',
      'cannot-send-notice-no-commission':
        'Für dieses Objekt ist keine Käuferprovision hinterlegt. Ohne Provision ist keine Widerrufsbelehrung nötig.',
      'cannot-send-notice-company-data-incomplete':
        'Ergänze Firmenname, Adresse, E-Mail und Telefon in den Unternehmensdaten, um die Widerrufsbelehrung zu senden.',
      'cannot-send-notice-privacy-url-missing':
        'Ergänze die Datenschutzerklärung (URL) in den Unternehmensdaten, um die Widerrufsbelehrung zu senden.',
      'cannot-send-notice-muster-not-signed-off':
        'Die Rechtstexte sind noch nicht freigegeben. Die Widerrufsbelehrung kann noch nicht gesendet werden.',
      'link-invalid': 'Dieser Link ist ungültig.',
      'cannot-conclude-notice-not-read': 'Bitte bestätigen Sie zuerst, dass Sie die Widerrufsbelehrung gelesen haben.',
      'cannot-conclude-page-outdated':
        'Diese Seite ist nicht mehr aktuell. Bitte prüfen Sie die Unterlagen erneut und schließen Sie dann ab.',
      'cannot-conclude-inactive': 'Dieses Angebot ist nicht mehr aktiv. Bei Fragen wenden Sie sich an {companyName}.',
      'expose-unavailable':
        'Das Exposé steht über diese Seite nicht zur Verfügung. Bei Fragen wenden Sie sich bitte direkt an uns.',
      'cannot-withdraw-required-fields-missing': 'Bitte füllen Sie Name, Vertrag und E-Mail-Adresse aus.',
      'cannot-withdraw-email-invalid': 'Bitte geben Sie eine gültige E-Mail-Adresse an.',
      'cannot-withdraw-period-ended':
        'Die Frist für einen Widerruf über diese Seite ist abgelaufen. Bei Fragen wenden Sie sich an {companyName}.',
      'cannot-withdraw-not-concluded': 'Für diesen Link gibt es keinen abgeschlossenen Vertrag.',
      'cannot-mark-acceptance-reply-not-concluded':
        'Die Annahme per E-Mail kannst du erst markieren, wenn der Maklervertrag online geschlossen ist.',
    },
    'buyer-contract-template': {
      'not-found': 'Diese Vorlage gibt es nicht mehr. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-save-block-empty': 'Fülle Maklervertrag, Widerrufsbelehrung und Muster-Widerrufsformular aus.',
      'already-adapted': 'Es gibt schon eine eigene Vorlage. Lade die Seite neu, um sie zu bearbeiten.',
    },
    'data-room': {
      'cannot-release-not-active': 'Nur für aktive Interessenten möglich.',
      'cannot-release-no-notice':
        'Sende zuerst die Widerrufsbelehrung. Erst dann gibt es einen persönlichen Link für die Unterlagen.',
      'cannot-release-not-concluded': 'Der Datenraum kann erst nach dem Maklervertrag freigegeben werden.',
      'cannot-release-withdrawal-received': 'Der Maklervertrag wurde widerrufen.',
      'cannot-release-inside-withdrawal-period':
        'Ohne vorzeitigen Beginn erst nach Ende der Widerrufsfrist am {withdrawalPeriodEndsOn, date, ::ddMMyyyy}.',
      'cannot-release-nothing-marked': 'Markiere zuerst Dokumente im Tab „Dokumente“ als „Für Käufer“.',
      'cannot-open-document-release-ended': 'Dieses Dokument ist nicht mehr freigegeben.',
    },
    'prospect-offer': {
      'cannot-record-amount-not-positive': 'Bitte gib einen Betrag größer als 0 € ein.',
      'cannot-record-date-in-future': 'Das Datum darf nicht in der Zukunft liegen.',
      'cannot-record-note-too-long': 'Die Notiz darf höchstens 500 Zeichen lang sein.',
      'cannot-record-financing-reservation-on-counter-offer':
        'Einen Finanzierungsvorbehalt gibt es nur bei einem Angebot des Kaufinteressenten.',
      'cannot-record-lost': 'Nimm den Interessenten zuerst wieder auf.',
      'cannot-accept-another-accepted':
        'Es gibt schon ein angenommenes Angebot. Öffne es zuerst wieder, bevor du ein anderes annimmst.',
      'cannot-change-status-not-allowed':
        'Dieses Angebot hat inzwischen einen anderen Status. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-change-status-lost': 'Nimm den Interessenten zuerst wieder auf.',
      'not-found': 'Dieses Angebot wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    prospect: {
      'cannot-create-property-not-eligible': 'Für dieses Objekt können keine Kaufinteressenten angelegt werden.',
      'cannot-invite-to-viewing-not-active':
        'Dieser Kaufprozess ist nicht mehr aktiv. Die Einladung wurde nicht verschickt.',
      'cannot-create-active-prospect-exists':
        'Für diesen Kontakt gibt es bei diesem Objekt schon einen laufenden Kaufprozess.',
      'not-found': 'Dieser Interessent wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-assess-viewing-attendance-missing': 'Bitte wähle, ob der Interessent erschienen ist.',
      'cannot-assess-viewing-impression-missing': 'Bitte wähle den Eindruck.',
      'cannot-assess-viewing-cancelled': 'Diese Besichtigung wurde abgesagt und kann nicht ausgewertet werden.',
      'cannot-assess-viewing-not-started':
        'Die Besichtigung hat noch nicht begonnen. Du kannst sie ab {startsAt, date, ::ddMMyyyy}, {startsAt, time, ::HHmm} auswerten.',
      'viewing-not-found':
        'Diese Besichtigung wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-advance-phase-not-later': 'Wähle eine spätere Phase. Zurück geht es über „Phase zurücksetzen“.',
      'cannot-advance-phase-notary-appointment-date-not-applicable':
        'Das Datum des Notartermins gibst du nur beim Wechsel in „Notartermin“ an.',
      'cannot-advance-phase-notary-appointment-date-required': 'Bitte gib das Datum des Notartermins an.',
      'cannot-advance-phase-offered-price-required': 'Bitte gib den angebotenen Kaufpreis an.',
      'cannot-advance-phase-purchase-price-required': 'Bitte gib den Kaufpreis laut Kaufvertrag an.',
      'cannot-advance-phase-property-reserved': 'Dieses Objekt ist bereits für einen anderen Interessenten reserviert.',
      'cannot-change-phase-lost': 'Nimm den Interessenten zuerst wieder auf.',
      'cannot-change-won': 'Abgeschlossene Kaufprozesse sind unveränderlich',
      'cannot-revert-phase-not-earlier':
        'Ein Interessent kann nur in eine frühere Phase zurückgesetzt werden. Lade die Seite neu, um die aktuelle Phase zu sehen.',
      'cannot-revert-phase-missing-reason': 'Bitte begründe den Rückschritt',
      'cannot-update-notary-appointment-date-lost': 'Nimm den Interessenten zuerst wieder auf.',
      'cannot-mark-lost-already-lost':
        'Dieser Interessent ist bereits als verloren markiert. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-reopen-not-lost':
        'Dieser Interessent ist nicht verloren und kann nicht wieder aufgenommen werden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-reopen-active-prospect-exists':
        'Für diesen Kontakt gibt es bei diesem Objekt schon einen laufenden Kaufprozess.',
      'cannot-reopen-property-reserved': 'Dieses Objekt ist bereits für einen anderen Interessenten reserviert.',
    },
    'record-move': {
      'outdated-preview':
        'Die Auswahl hat sich seit der Vorschau geändert. Prüfe die neue Vorschau und bestätige erneut.',
      'records-not-in-source':
        'Einige ausgewählte Datensätze gehören nicht mehr zu diesem Standort. Lade die Seite neu und wähle erneut aus.',
      'source-organization-not-found': 'Dieser Standort wurde nicht gefunden.',
      'target-inactive': 'Der Ziel-Standort ist inaktiv. Wähle einen aktiven Standort.',
      'target-is-source': 'Ziel-Standort und aktueller Standort sind identisch. Wähle einen anderen Standort.',
      'target-not-in-workspace': 'Der Ziel-Standort gehört nicht zu diesem Workspace.',
    },
  },
  contact: {
    'contact-communication': {
      'cannot-record-content-too-long': 'Eine Aktivität darf höchstens 10.000 Zeichen lang sein.',
      'cannot-record-duration-out-of-range': 'Bitte gib eine gültige Dauer in Minuten an.',
      'cannot-record-empty-content': 'Bitte gib einen Inhalt ein.',
      'cannot-record-future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'cannot-record-subject-too-long': 'Der Betreff darf höchstens 200 Zeichen lang sein.',
      'not-found': 'Diese Kommunikation wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'contact-note': {
      'cannot-record-body-too-long': 'Eine Aktivität darf höchstens 10.000 Zeichen lang sein.',
      'cannot-record-empty-body': 'Bitte gib einen Inhalt ein.',
      'cannot-record-future-occurred-at': '„Stattgefunden am" kann nicht in der Zukunft liegen.',
      'not-found': 'Diese Notiz wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    contact: {
      'cannot-create-company-name-required': 'Bitte gib einen Firmennamen ein.',
      'cannot-create-invalid-date-of-birth':
        'Bitte gib ein gültiges Geburtsdatum ein: Es muss in der Vergangenheit liegen und darf höchstens 120 Jahre zurückliegen.',
      'cannot-create-invalid-type': 'Einer der gewählten Typen ist ungültig. Wähle nur Typen aus der Liste.',
      'cannot-create-multiple-primary-emails':
        'Es kann nur eine primäre E-Mail-Adresse geben. Markiere höchstens eine als primär.',
      'cannot-create-multiple-primary-phones':
        'Es kann nur eine primäre Telefonnummer geben. Markiere höchstens eine als primär.',
      'cannot-create-person-name-required': 'Bitte gib einen Vor- und einen Nachnamen ein.',
      'cannot-create-profile-mismatch':
        'Die Angaben passen nicht zum Kontakttyp: Eine Person hat Vor- und Nachnamen, eine Firma einen Firmennamen. Prüfe den gewählten Kontakttyp.',
      'not-found': 'Dieser Kontakt wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reassign-target-organization-not-found':
        'Der gewählte Standort wurde nicht gefunden. Lade die Seite neu und wähle einen anderen Standort.',
    },
  },
  company: {
    'company-contact': {
      'cannot-link-duplicate':
        'Diese Firma ist mit dem Kontakt bereits verknüpft. Du kannst die Rolle der bestehenden Verknüpfung bearbeiten.',
      'cannot-link-missing-role': 'Bitte gib eine Rolle ein.',
      'cannot-link-non-person':
        'Mit einer Firma lassen sich nur Kontakte vom Typ Person verknüpfen. Wähle eine Person aus.',
      'cannot-link-role-too-long': 'Rolle darf höchstens 200 Zeichen lang sein.',
      'link-not-found': 'Diese Verknüpfung wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    company: {
      'cannot-archive-already-archived':
        'Diese Firma ist bereits archiviert. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-create-invalid-type': 'Der gewählte Typ ist ungültig. Wähle einen Typ aus der Liste.',
      'cannot-create-name-required': 'Bitte gib einen Firmennamen ein.',
      'cannot-create-name-too-long': 'Firmenname darf höchstens 120 Zeichen lang sein.',
      'cannot-create-notes-too-long': 'Die Notizen dürfen höchstens 5.000 Zeichen lang sein.',
      'cannot-create-tag-too-long': 'Jeder Tag darf höchstens 200 Zeichen lang sein.',
      'cannot-unarchive-not-archived':
        'Diese Firma ist nicht archiviert. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'not-found': 'Diese Firma wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
  },
  webhooks: {
    webhook: {
      'illegal-transition':
        'Dieser Verarbeitungsschritt ist für die Webhook-Benachrichtigung in ihrem aktuellen Status nicht möglich.',
      'invalid-json': 'Der Inhalt der Webhook-Benachrichtigung ist kein gültiges JSON. Sie wurde nicht angenommen.',
    },
  },
  // Die neutrale 404 der organisationsadressierten Routen (`require-workspace-organization.ts`).
  // Absichtlich identisch mit `identity.organization.not-found`: eine fremde und eine
  // unbekannte Organisations-ID dürfen nicht unterscheidbar sein.
  organization: {
    'not-found': 'Diese Organisation wurde nicht gefunden.',
  },
  media: {
    file: {
      'upload-not-allowed': 'Diese Datei ist für diesen Zweck nicht erlaubt.',
      'invalid-content': 'Die hochgeladene Datei ist ungültig.',
      'not-found': 'Diese Datei wurde nicht gefunden.',
      'object-missing': 'Es wurde keine hochgeladene Datei gefunden.',
    },
  },
  fleet: {
    machine: {
      'not-found': 'Diese Maschine gibt es nicht.',
      'approval-unchanged': 'Diese Maschine hat bereits den gewünschten Status.',
      'not-approved': 'Diese Maschine ist nicht freigegeben. Gib sie frei, bevor du sie steuerst.',
    },
    account: {
      'not-found': 'Dieses Konto gibt es nicht. Der Worker hat es vielleicht getrennt.',
    },
    command: {
      'not-found': 'Dieser Befehl wurde nicht gefunden.',
      'timed-out': 'Die Maschine hat nicht rechtzeitig geantwortet. Bitte versuche es erneut.',
    },
    log: {
      'stream-unavailable': 'Für dieses Profil gibt es gerade kein Protokoll dieser Art.',
    },
    job: {
      'already-claimed': '{worker} arbeitet schon an {key}. Es kann immer nur eine Maschine daran arbeiten.',
      'worker-busy':
        'Dieser Worker hält bereits {key}. Er kann erst etwas Neues übernehmen, wenn das abgeschlossen ist.',
      blocked: '{key} wartet auf eine Entscheidung: {reason}. Solange bleibt die Arbeit liegen.',
      'run-limit':
        '{key} ist bereits {runs}-mal gelaufen – das Maximum sind {limit} Läufe. Das Ticket muss von Hand abgeschlossen werden.',
      'weekly-limit':
        '{machine} hat ihr Wochenlimit erreicht: {usagePercent} % genutzt, Limit {limitPercent} %. Bis zum Reset übernimmt die Maschine keine neuen Jobs.',
      'not-found': 'Diesen Job gibt es nicht.',
      'not-held': 'Dieser Job ist nicht mehr beansprucht.',
      'claim-lost': 'Dieser Job gehört inzwischen einer anderen Maschine. Deine Runde wurde freigegeben.',
      'question-not-allowed': 'Eine Frage kann nur ein geparkter Job mitbringen.',
      'ticket-write-not-allowed': 'Ein Verschieben gehört zu einem beendeten Job, ein Parken zu einem geparkten.',
      bound:
        '{key} ist {worker} zugeordnet und bleibt es, bis die Zuordnung von Hand aufgehoben wird. Solange darf kein anderer Worker daran arbeiten.',
    },
    question: {
      'not-found': 'Diese Frage gibt es nicht.',
      'already-answered': 'Diese Frage wurde bereits beantwortet.',
      'not-open': 'Diese Frage ist nicht mehr offen: Das Ticket wurde inzwischen erneut aufgenommen.',
      'messenger-not-configured': 'Für Fleet-Fragen ist kein Messenger eingerichtet.',
    },
    report: {
      'not-found': 'Diese Meldung gibt es nicht.',
      'not-held': 'Diese Meldung wird nicht von {worker} bearbeitet.',
      closed: 'Diese Meldung ist bereits abgeschlossen.',
    },
    'assistant-turn': {
      'not-found': 'Diese Nachricht an den Fleet-Assistenten gibt es nicht.',
      'not-held': 'Diese Nachricht beantwortet gerade eine andere Maschine, oder sie ist schon abgeschlossen.',
      'out-of-context':
        'Diese Antwort gehört nicht zu der Frage, um die es im Gespräch geht. Es wurde nichts eingetragen.',
    },
    queue: {
      'team-not-found': 'Im Tracker gibt es kein Team mit diesem Namen.',
      'tracker-unreachable': 'Linear konnte nicht gelesen werden. Bitte versuche es gleich noch einmal.',
      'tracker-not-configured': 'Für den Tracker ist kein Linear-Schlüssel hinterlegt.',
      'not-in-queue': 'Dieses Ticket steht nicht mehr an dieser Stelle der Warteschlange. Das Board wird neu geladen.',
    },
    tick: {
      'too-many-lines':
        'Diese Maschine hat {sent} Zeilen auf einmal geschickt, erlaubt sind {max}. Der Rest folgt beim nächsten Mal.',
      'too-many-records':
        'Diese Maschine hat {sent} Datensätze auf einmal geschickt, erlaubt sind {max}. Der Rest folgt beim nächsten Mal.',
    },
  },
  property: {
    listing: {
      'not-found': 'Dieses Objekt wurde nicht gefunden.',
    },
    organization: {
      'not-in-workspace': 'Dieser Standort gehört nicht zu deinem Workspace.',
    },
    property: {
      'not-found': 'Dieses Objekt wurde nicht gefunden.',
      'cannot-activate':
        'Die Vermarktung kann nicht gestartet werden — es fehlen Pflichtfelder für die Veröffentlichung. Bitte ergänze sie auf der Objekt-Detailseite.',
      'invalid-status-transition': 'Dieser Statuswechsel ist für das Objekt nicht möglich.',
      'status-conflict': 'Der Status des Objekts hat sich geändert — bitte lade die Seite neu und versuche es erneut.',
      'reference-number-taken': 'Diese Exposé-Nr. wird bereits verwendet — bitte wähle eine eindeutige Nummer.',
      'archive-reason-required': 'Bitte wähle einen Archivierungsgrund.',
      'archive-reason-marketing-type-mismatch':
        'Dieser Archivierungsgrund passt nicht zur Vermarktungsart des Objekts.',
      'archive-note-required': 'Bitte gib bei „Sonstiges“ eine kurze Notiz an.',
      'archive-note-too-long': 'Die Notiz darf höchstens 280 Zeichen lang sein.',
      'archive-open-deal-not-losable':
        'Du kannst dieses Objekt nicht archivieren, weil du den offenen Deal nicht als verloren markieren darfst.',
      'archive-loss-reason-required':
        'Dieses Objekt hat noch einen offenen Deal. Wähle einen Verlustgrund, damit er beim Archivieren als verloren markiert wird.',
      'reference-requires-sold': 'Referenzobjekte lassen sich nur für verkaufte Objekte festlegen.',
      'homepage-requires-reference': 'Bitte markiere das Objekt zuerst als Referenzobjekt.',
      'reservation-requires-active': 'Nur aktive Objekte können reserviert werden. Bitte lade die Seite neu.',
      'energy-class-off-scale':
        'Für ein Gewerbeobjekt mit einem Energieausweis ab 2027 gilt die Skala A bis G. Wähle eine Klasse von A bis G.',
      'use-split-exceeds-usable-area':
        'Die Teilflächen dürfen die Nutzfläche nicht übersteigen — {sum} m² von {usableArea} m².',
      'commission-rate-out-of-range': 'Gib einen Satz zwischen 0 und 100 % ein.',
      'commission-amount-not-positive': 'Gib einen Betrag größer als 0 € ein.',
      'pricing-not-applicable':
        '{expected, select, SALE {Dieses Objekt wird nicht verkauft — Kaufpreise lassen sich hier nicht erfassen.} RENTAL {Dieses Objekt wird nicht vermietet — Mietpreise lassen sich hier nicht erfassen.} other {Dieser Preistyp passt nicht zur Vermarktungsart dieses Objekts.}}',
      'marketing-type-change-blocked-by-deal':
        'Zu diesem Objekt läuft ein Deal. Die Vermarktungsart lässt sich erst ändern, wenn der Deal abgeschlossen oder verloren ist.',
      'delete-status-conflict':
        'Der Status des Objekts hat sich inzwischen geändert, deshalb wurde es nicht gelöscht. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'investment-yield-not-applicable': 'Ertragsdaten lassen sich nur für ein Mehrfamilienhaus erfassen.',
      'object-type-change-blocked':
        'Dieses Objekt hat noch typspezifische Daten. Entferne sie zuerst, um den Objekttyp zu ändern.',
      'object-type-locked': 'Der Objekttyp kann nur im Entwurf geändert werden.',
      'reassign-target-organization-not-found':
        'Der gewählte Standort wurde nicht gefunden. Lade die Seite neu und wähle einen anderen Standort.',
    },
    fact: {
      'provenance-missing': 'Zu {factKey} fehlen Herkunft, Status oder Prüfstufe.',
      'key-retired': 'Das Merkmal {factKey} ist stillgelegt und nimmt keine neuen Werte an.',
      'occurrence-required':
        '{factKey} kommt mehrfach vor. Gib an, zu welchem Vorkommen der Wert gehört, zum Beispiel „Einheit 2“.',
      'occurrence-not-allowed': '{factKey} kommt nur einmal vor und hat kein Vorkommen.',
      'value-required':
        'Zu {factKey} fehlt der Wert. Ohne Wert geht es nur mit dem Status „unbekannt“ oder „nicht anwendbar“.',
      'reason-required': 'Gib zu {factKey} an, warum der Wert unbekannt ist oder nicht zutrifft.',
      'external-evidence-required': 'Ein externer Wert zu {factKey} nennt seine ursprüngliche Quelle und ihr Datum.',
      'intermediary-as-source': 'Nenne zu {factKey} die ursprüngliche Quelle, nicht „{source}“.',
      'assessment-needs-source':
        '{factKey} ist eine Einschätzung der Lage und braucht eine genannte Quelle oder deine eigene Aussage.',
      'confirmation-needs-member': 'Nur ein Mitglied kann {factKey} als „Makler bestätigt“ erfassen.',
      'unknown-subfield': '{factKey} hat kein Unterfeld „{subfield}“.',
      'not-found': 'Dieser Wert ist nicht mehr aktuell. Die Liste zeigt jetzt den neuesten Stand.',
      'conflicting-values': 'Du hast für ein Objektfeld zwei verschiedene Werte ausgewählt. Bestätige nur einen davon.',
      'replace-not-agreed': 'Ein Objektfeld hat inzwischen einen anderen Wert. Prüf die Übernahme bitte noch einmal.',
      'entered-by-member':
        'Diesen Wert hat ein Mitglied selbst erfasst. Die Bestätigung lässt sich nicht zurücknehmen.',
    },
    'immoos-iq': {
      'address-incomplete': 'Für den Abruf braucht das Objekt eine vollständige Adresse.',
      unreachable: 'immoOS IQ ist gerade nicht erreichbar. Bitte versuche es später erneut.',
    },
    'agent-finding': {
      'field-unavailable': 'Das Merkmal {factKey} gibt es im Katalog nicht oder es ist stillgelegt.',
      'provenance-missing': 'Zu {factKey} fehlen Herkunft oder Status.',
      'evidence-document-not-found': 'Das Dokument, das {factKey} belegen soll, gibt es an diesem Objekt nicht.',
    },
    'fact-key': {
      'not-found': 'Dieser Merkmalschlüssel wurde nicht gefunden.',
      'personal-data-not-public': 'Personenbezogene Angaben können nicht veröffentlicht werden.',
      incomplete: 'Bitte fülle Bezeichnung, Gruppe, Werttyp, Häufigkeit und mindestens eine Objektart aus.',
      'invalid-id': 'Die ID muss aus Gruppenkürzel und Namen bestehen, zum Beispiel FLA.WOHNFLAECHE.',
      'id-taken':
        'Diese ID gibt es schon. Auch stillgelegte IDs lassen sich nicht wiederverwenden. Bitte wähle eine andere.',
      'already-retired': 'Dieses Merkmal ist schon stillgelegt.',
      'retired-has-no-need': 'Ein stillgelegtes Merkmal hat keinen Bedarf mehr.',
      'need-ambiguous':
        'Dieser Bedarf widerspricht einem gleich genauen Eintrag: Ein Objekt kann beide erfüllen, und keiner ist genauer. Lege einen Eintrag mit beiden Bedingungen an oder gleiche die Stufen an.',
    },
    'fact-key-group': {
      'not-found': 'Diese Gruppe gibt es im Katalog nicht. Vorhandene Gruppen: {groups}.',
    },
    'property-media': {
      'cannot-add-capture-photo': 'Aufnahmefotos können nicht in die Galerie übernommen werden.',
      'cover-image-always-published': 'Das Titelbild wird immer veröffentlicht. Bestimme zuerst ein anderes Titelbild.',
      'cover-image-not-a-photo': 'Nur ein Foto kann als Titelbild gesetzt werden.',
      'gallery-full':
        'Ein Objekt kann höchstens 100 Medien enthalten. Lösche zuerst andere Medien, um Platz zu schaffen.',
      'image-too-large': 'Bilder dürfen maximal 20 MB groß sein.',
      'manage-denied':
        'Dazu fehlt dir die Berechtigung: Du kannst nur Medien verwalten, die du selbst hochgeladen hast.',
      'not-an-image': 'Nur Bilder lassen sich als Foto oder Grundriss einordnen oder in die Dokumente verschieben.',
      'not-found': 'Dieses Medium wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reorder-mismatch':
        'Die Medien in diesem Bereich haben sich inzwischen geändert. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'type-not-allowed': 'Nur JPG, PNG, WebP und MP4 sind erlaubt.',
      'video-link-duplicate': 'Dieses Video ist schon hinterlegt.',
      'video-link-not-public':
        'Dieses Video ist nicht öffentlich abrufbar. Bitte prüfe die Freigabe bei YouTube oder Vimeo.',
      'video-link-unsupported': 'Bitte gib einen Link zu einem YouTube- oder Vimeo-Video ein.',
      'video-provider-unreachable':
        'Das Video konnte gerade nicht geprüft werden. Bitte versuche es später noch einmal.',
      'video-too-large': 'Videos dürfen maximal 200 MB groß sein.',
    },
    'property-capture': {
      'already-started': 'Für dieses Objekt läuft schon eine Objektaufnahme.',
      'field-not-asked': 'Dieses Feld wird in der Objektaufnahme nicht abgefragt.',
      'motif-not-applicable': 'Dieses Motiv gehört nicht zum Fotoprotokoll dieser Objektart.',
      'not-a-capture-upload':
        'Dieses Foto wurde nicht für die Objektaufnahme hochgeladen. Nimm es bitte noch einmal auf.',
      'not-found': 'Für dieses Objekt gibt es noch keine Objektaufnahme.',
      'photo-limit-reached': 'Diese Objektaufnahme hat schon {limit} Fotos. Mehr passen nicht hinein.',
      'object-type-not-supported': 'Eine Objektaufnahme gibt es nur für Häuser, Wohnungen und Grundstücke.',
    },
    'property-tour': {
      'invalid-link': 'Bitte gib einen gültigen Link ein, der mit https:// beginnt.',
      'limit-reached': 'Höchstens {limit} Rundgänge pro Objekt.',
      'not-found': 'Dieser Rundgang wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'reorder-mismatch':
        'Die Rundgänge haben sich inzwischen geändert. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'property-descriptions': {
      'too-long': 'Dieser Text ist zu lang. Bitte kürze ihn auf höchstens 20.000 Zeichen.',
      'stale-edit':
        'Jemand anderes hat diesen Text gerade geändert. Lade die Seite neu, damit du auf dem aktuellen Stand arbeitest.',
      'expose-title-too-long': 'Der Exposé-Titel ist zu lang. Bitte kürze ihn auf höchstens 120 Zeichen.',
      'highlight-too-long': 'Dieses Highlight ist zu lang. Bitte kürze es auf höchstens 80 Zeichen.',
    },
    'document-analysis': {
      'field-not-found':
        'Dieses Feld ist in der aktuellen Analyse nicht enthalten. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'missing-prompt': 'Die Dokumentanalyse ist gerade nicht verfügbar. Bitte wende dich an den immoOS-Support.',
      'not-found':
        'Zu diesem Dokument wurde keine Analyse gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'document-request': {
      'no-owner-email':
        'Für dieses Objekt ist kein Eigentümer mit E-Mail-Adresse hinterlegt. Verknüpfe zuerst einen Kontakt mit E-Mail-Adresse als Eigentümer.',
    },
    'memory-entry': {
      'not-found': 'Dieser Memory-Eintrag wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-change-orphaned':
        'Diese Aussage steht nicht mehr in ihrer Quelle. Du kannst die verwaiste Änderung nur noch löschen.',
    },
    'property-contact': {
      'already-linked':
        'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
      'not-linked':
        'Dieser Kontakt ist in dieser Rolle nicht mit dem Objekt verknüpft. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'role-marketing-mismatch': 'Diese Rolle passt nicht zur Vermarktungsart der Immobilie.',
    },
    'property-document': {
      'manage-denied':
        'Dazu fehlt dir die Berechtigung: Du kannst nur Dokumente verwalten, die du selbst hochgeladen hast.',
      'not-found': 'Dieses Dokument wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
      'cannot-delete-signed': 'Unterschriebene Dokumente können nicht gelöscht werden.',
      'cannot-file-capture-photo': 'Aufnahmefotos können nicht als Dokument abgelegt werden.',
      'cannot-file-expose-upload':
        'Diese Datei wurde als Exposé hochgeladen und kann nicht als Dokument abgelegt werden.',
      'cannot-share-personal-document':
        'Persönliche Unterlagen wie Selbstauskunft oder Ausweis können nicht für Käufer freigegeben werden.',
      'cannot-share-valuation-document':
        'Bewertungen, Wertgutachten und Akquisebriefings können nicht für Käufer freigegeben werden.',
      'cannot-recategorize-shared-with-buyers': 'Nimm das Dokument zuerst aus „Für Käufer“ heraus.',
    },
    'property-expose': {
      'not-a-pdf': 'Exposés können nur als PDF hochgeladen werden.',
      'too-large': 'Die Datei darf maximal 20 MB groß sein.',
      'not-an-expose-upload': 'Diese Datei wurde nicht als Exposé hochgeladen. Lade sie bitte erneut als Exposé hoch.',
      'upload-already-used':
        'Diese Datei ist bereits als anderes Exposé hinterlegt. Lade sie für diese Version bitte erneut hoch.',
      'not-found':
        'Für diese Version ist kein Exposé mehr hinterlegt. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'portal-connection': {
      'not-connected':
        '{portal, select, KLEINANZEIGEN {Der Workspace ist nicht mit Kleinanzeigen verbunden. Ein Eigentümer oder Admin kann den Zugang unter ‚Portale‘ eintragen.} IMMOWELT {Der Workspace ist nicht mit Immowelt verbunden. Ein Eigentümer oder Admin kann den Zugang unter ‚Portale‘ eintragen.} other {Der Workspace ist nicht mit ImmoScout24 verbunden. Ein Eigentümer oder Admin kann das Konto unter ‚Portale‘ verbinden.}}',
      expired:
        '{portal, select, KLEINANZEIGEN {Der Zugang zu Kleinanzeigen ist ungültig. Veröffentlichen ist erst wieder möglich, wenn ein Eigentümer oder Admin den Zugang unter ‚Portale‘ korrigiert hat.} IMMOWELT {Der Zugang zu Immowelt ist ungültig. Veröffentlichen ist erst wieder möglich, wenn ein Eigentümer oder Admin den Zugang unter ‚Portale‘ korrigiert hat.} other {Die ImmoScout24-Verbindung ist abgelaufen. Veröffentlichen ist erst nach dem Erneuern wieder möglich.}}',
      unavailable:
        '{portal, select, KLEINANZEIGEN {Die Kleinanzeigen-Anbindung} IMMOWELT {Die Immowelt-Anbindung} other {Die ImmoScout24-Anbindung}} ist in dieser Umgebung nicht eingerichtet.',
      'attempt-invalid': 'Der Verbindungsvorgang ist abgelaufen. Starte ihn unter ‚Portale‘ noch einmal.',
      'consent-declined':
        'ImmoScout24 hat den Zugriff nicht freigegeben. Starte die Verbindung noch einmal und bestätige sie bei ImmoScout24.',
      'provider-failed': 'ImmoScout24 ist gerade nicht erreichbar. Versuche es in ein paar Minuten noch einmal.',
      'sign-in-failed':
        'Anmeldung beim Server von {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {dem Portal}} fehlgeschlagen. Bitte prüfe Server, Benutzername und Passwort.',
      'password-required': 'Gib das Passwort ein, um Server, Benutzername oder Anbieter-ID zu ändern.',
    },
    'portal-listing': {
      'property-not-active':
        'Dieses Objekt ist noch nicht in Vermarktung. Veröffentlichen ist erst nach Erhalt des Maklervertrags möglich.',
      'not-ready':
        '{portal, select, KLEINANZEIGEN {Das Objekt ist noch nicht bereit für Kleinanzeigen. Öffne ‚Auf Kleinanzeigen veröffentlichen‘, um zu sehen, was noch fehlt.} IMMOWELT {Das Objekt ist noch nicht bereit für Immowelt. Öffne ‚Auf Immowelt veröffentlichen‘, um zu sehen, was noch fehlt.} other {Das Objekt ist noch nicht bereit für ImmoScout24. Öffne ‚Auf ImmoScout24 veröffentlichen‘, um zu sehen, was noch fehlt.}}',
      'contact-incomplete':
        'Für {memberName} fehlen Angaben für den {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}}-Kontakt. Ergänze das Profil und versuche es noch einmal.',
      'responsible-member-inactive':
        'Das zuständige Mitglied ist nicht mehr aktiv. Weise das Objekt einem aktiven Mitglied zu, bevor du es auf {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}} veröffentlichst.',
      'listing-type-changed':
        'Das Objekt passt nicht mehr zu seinem ImmoScout24-Inserat, weil sich Untertyp oder Vermarktungsart geändert haben. Ziehe das Inserat zuerst zurück.',
      'operation-in-progress':
        'Das Inserat wird gerade übertragen. Warte einen Moment und versuche es dann noch einmal.',
      'not-live':
        'Dieses Objekt hat kein Inserat, das auf {portal, select, KLEINANZEIGEN {Kleinanzeigen} IMMOWELT {Immowelt} other {ImmoScout24}} online ist.',
      'attach-not-found': 'Unter dieser ID gibt es im verbundenen ImmoScout24-Konto kein Inserat.',
      'attach-type-mismatch':
        'Das ImmoScout24-Inserat unter dieser ID hat eine andere Objektart als dieses Objekt und kann nicht übernommen werden.',
      'attach-taken': 'Dieses ImmoScout24-Inserat gehört bereits zum Objekt {objectNumber}.',
      'attach-taken-unnumbered':
        'Dieses ImmoScout24-Inserat gehört bereits zu einem anderen Objekt in deinem Workspace.',
      'already-on-portal':
        'Dieses Objekt hat bereits ein eigenes ImmoScout24-Inserat. Ein weiteres kann nicht übernommen werden.',
    },
    'browser-import': {
      'not-allowed': 'Du darfst keine Inserate aus ImmoScout24 importieren.',
      'unreadable-listing': 'Dieses Inserat lässt sich nicht übernehmen: Art oder Adresse fehlen.',
      'assignee-not-in-standort': 'Die zuständige Person gehört nicht zu diesem Standort.',
      'picture-not-uploaded': 'Ein Bild dieses Inserats wurde nicht hochgeladen. Versuch es noch einmal.',
    },
    'browser-run': {
      'api-connection-in-use':
        'Dein Workspace ist über die ImmoScout24-Schnittstelle verbunden. Veröffentliche dort mit „Veröffentlichen“.',
      'already-open': 'Für dieses Objekt läuft schon eine Übertragung an ImmoScout24. Warte, bis sie fertig ist.',
      'listing-type-required': 'Dieses Objekt hat zwei Entwürfe auf ImmoScout24. Gib an, welcher online gehen soll.',
      'publication-option-required': 'Wähle mindestens eine Option, wo ImmoScout24 das Inserat veröffentlicht.',
      'publication-option-not-applicable': 'Diese Anfrage stellt nichts online, daher gibt es keine Option zu wählen.',
      'not-a-draft': 'Dieses Inserat ist kein Entwurf auf ImmoScout24. Online stellen geht nur bei einem Entwurf.',
      'not-found': 'Diese Übertragung an ImmoScout24 gibt es nicht mehr. Lade die Seite neu.',
      'not-live': 'Dieses Inserat ist auf ImmoScout24 nicht online.',
      'still-live':
        'Dieses Inserat ist auf ImmoScout24 noch online. Nimm es zuerst offline, dann kannst du es löschen.',
      'not-running': 'Diese Übertragung an ImmoScout24 läuft nicht mehr.',
      'not-awaiting-confirmation': 'Über diese Anfrage wurde schon entschieden. Lade die Seite neu.',
      'not-confirmed': 'Diese Anfrage kann nicht mehr ausgeführt werden.',
    },
    'document-redaction': {
      'engine-failed': 'Das Schwärzen ist fehlgeschlagen. Bitte versuche es erneut.',
      'region-outside-document':
        'Eine Markierung liegt außerhalb des Dokuments. Lade die Seite neu und versuche es erneut.',
      'unsupported-type': 'Nur PDFs und Bilder können geschwärzt werden',
      'not-found':
        'Dieser Schwärzungsvorgang wurde nicht gefunden. Lade die Seite neu, um den aktuellen Stand zu sehen.',
    },
    'web-expose': {
      'property-not-active':
        'Dieses Objekt ist noch nicht in Vermarktung. Veröffentlichen ist erst nach Erhalt des Maklervertrags möglich.',
      'not-for-sale': 'Ein Web-Exposé gibt es nur für Objekte zum Kauf.',
      'not-ready': 'Das Web-Exposé ist noch nicht bereit. Öffne ‚Veröffentlichen‘, um zu sehen, was noch fehlt.',
      'not-published': 'Dieses Web-Exposé ist nicht veröffentlicht.',
      unavailable: 'Dieses Exposé ist nicht mehr verfügbar.',
    },
    'reference-feed': {
      'workspace-not-found': 'Für diesen Link wurde kein Workspace gefunden. Prüfe die Adresse des Referenz-Feeds.',
    },
  },
} as const satisfies ErrorMessages;
